import * as z from 'zod';
import { useSnackbars } from '@customerio/pluma-components/react';
import { EXPIRED_SESSION_SNACKBAR_KEY } from '@/features/account-switcher/constants';
import { useMountEffect } from '@/hooks/state/use-mount-effect';

export const ExpiredSessionSnackbar = z.object({
  name: z.string(),
});

function getExpiredSessionSnackbar(): z.infer<typeof ExpiredSessionSnackbar> | null {
  try {
    const raw = sessionStorage.getItem(EXPIRED_SESSION_SNACKBAR_KEY);
    if (raw == null) {
      return null;
    }
    const json: z.infer<typeof ExpiredSessionSnackbar> = JSON.parse(raw);
    const parsed = ExpiredSessionSnackbar.safeParse(json);
    if (!parsed.success) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  } finally {
    sessionStorage.removeItem(EXPIRED_SESSION_SNACKBAR_KEY);
  }
}

/**
 * After a successful fallback reload (switching away from an expired session),
 * checks sessionStorage for a pending snackbar message and displays it.
 *
 * Shows a snackbar like "Your session for Acme Corp has expired" with a
 * "Log in again" CTA that navigates to the login page.
 */
export function useExpiredSessionSnackbar(): void {
  const snackbars = useSnackbars();

  useMountEffect(() => {
    const expiredSession = getExpiredSessionSnackbar();
    if (expiredSession == null) {
      return;
    }

    snackbars.create({
      message: `Your session for ${expiredSession.name} has expired`,
      variant: 'error',
      action: {
        label: 'Log in again',
        href: '/login?addAccount=true',
        isExternal: true,
      },
    });
  });
}
